import React, { useState } from "react";
import { Menu, MenuItem, Stack } from "@mui/material";
import { Visibility, Edit, Delete, RestoreFromTrash, CheckCircle, Cancel, PictureAsPdf } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import ModelDelete from "../Models/ModelDelete";

const TableMenu = ({
    anchorEl,
    open,
    onClose,
    onView,
    onRestore,
    onEdit,
    onDelete,
    onConfirm,
    onCloseAction,
    onExport,
    isDeletingSingle,

    disableRestore = false,
    disableEdit = false,
    disableDelete = false,
    disableConfirm = false,
    disableClose = false,
    disableExport = false,
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";

    const [confirmOpen, setConfirmOpen] = useState(false);

    if (!anchorEl) return null;

    const handleOpenConfirm = () => {
        if (!onDelete || disableDelete || isDeletingSingle) return;
        setConfirmOpen(true);
    };

    const handleCloseConfirm = () => {
        if (isDeletingSingle) return;
        setConfirmOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!onDelete || disableDelete) return;
        await onDelete();
        setConfirmOpen(false);
        onClose?.();
    };

    const handleRestore = () => {
        if (!onRestore || disableRestore) return;
        onRestore();
        onClose?.();
    };

    const handleEdit = () => {
        if (!onEdit || disableEdit) return;
        onEdit();
        onClose?.();
    };

    const handleView = () => {
        if (!onView) return;
        onView();
        onClose?.();
    };

    const handleConfirm = () => {
        if (!onConfirm || disableConfirm) return;
        onConfirm();
        onClose?.();
    };

    const handleCloseAction = () => {
        if (!onCloseAction || disableClose) return;
        onCloseAction();
        onClose?.();
    };

    const handleExport = () => {
        if (!onExport || disableExport) return;
        onExport();
        onClose?.();
    };

    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={onClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: isArabic ? "left" : "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: isArabic ? "left" : "right",
                }}
                MenuListProps={{
                    "aria-label": isArabic ? "قائمة الإجراءات" : "actions menu",
                }}
            >
                {onView && (
                    <MenuItem onClick={handleView}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Visibility fontSize="small" color="primary" />
                            <span>{isArabic ? "عرض" : "View"}</span>
                        </Stack>
                    </MenuItem>
                )}

                {onEdit && (
                    <MenuItem
                        onClick={handleEdit}
                        disabled={isDeletingSingle || disableEdit}
                        title={
                            disableEdit
                                ? isArabic
                                    ? "لا تملك صلاحية التعديل"
                                    : "No edit permission"
                                : ""
                        }
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Edit fontSize="small" color="warning" />
                            <span>{isArabic ? "تعديل" : "Edit"}</span>
                        </Stack>
                    </MenuItem>
                )}

                {onRestore && (
                    <MenuItem
                        onClick={handleRestore}
                        disabled={isDeletingSingle || disableRestore}
                        title={
                            disableRestore
                                ? isArabic
                                    ? "لا تملك صلاحية الاستعادة"
                                    : "No restore permission"
                                : ""
                        }
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <RestoreFromTrash fontSize="small" color="success" />
                            <span>{isArabic ? "استعادة" : "Restore"}</span>
                        </Stack>
                    </MenuItem>
                )}

                {onConfirm && (
                    <MenuItem
                        onClick={handleConfirm}
                        disabled={isDeletingSingle || disableConfirm}
                        title={
                            disableConfirm
                                ? isArabic
                                    ? "لا تملك صلاحية التأكيد"
                                    : "No confirm permission"
                                : ""
                        }
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CheckCircle fontSize="small" color="success" />
                            <span>{isArabic ? "تأكيد" : "Confirm"}</span>
                        </Stack>
                    </MenuItem>
                )}

                {onCloseAction && (
                    <MenuItem
                        onClick={handleCloseAction}
                        disabled={isDeletingSingle || disableClose}
                        title={
                            disableClose
                                ? isArabic
                                    ? "لا تملك صلاحية الإغلاق"
                                    : "No close permission"
                                : ""
                        }
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Cancel fontSize="small" color="error" />
                            <span>{isArabic ? "إغلاق" : "Close"}</span>
                        </Stack>
                    </MenuItem>
                )}

                {onExport && (
                    <MenuItem
                        onClick={handleExport}
                        disabled={isDeletingSingle || disableExport}
                        title={
                            disableExport
                                ? isArabic
                                    ? "لا تملك صلاحية التصدير"
                                    : "No export permission"
                                : ""
                        }
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <PictureAsPdf fontSize="small" color="primary" />
                            <span>{isArabic ? "تصدير PDF" : "Export PDF"}</span>
                        </Stack>
                    </MenuItem>
                )}

                {onDelete && (
                    <MenuItem
                        onClick={handleOpenConfirm}
                        disabled={isDeletingSingle || disableDelete}
                        title={
                            disableDelete
                                ? isArabic
                                    ? "لا تملك صلاحية الحذف"
                                    : "No delete permission"
                                : ""
                        }
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Delete fontSize="small" color="error" />
                            <span>{isArabic ? "حذف" : "Delete"}</span>
                        </Stack>
                    </MenuItem>
                )}
            </Menu>

            <ModelDelete
                confirmOpen={confirmOpen}
                handleCloseConfirm={handleCloseConfirm}
                isArabic={isArabic}
                isDeletingSingle={isDeletingSingle}
                handleConfirmDelete={handleConfirmDelete}
            />
        </>
    );
};

export default TableMenu;
